import axios, { AxiosInstance } from 'axios';
import type { AblRequest, AblResponse } from '../types/api.types';
import { configService } from './config.service';
import { identityService } from './identity.service';
import { sanitizeForLatin2 } from '../utils/latin2';

// Priority: configService (localStorage / okienko konfiguracji) > VITE_API_URL (.env) > brak domyślnego adresu
const ENV_API_URL = import.meta.env.VITE_API_URL;
const ENV_API_KEY = import.meta.env.VITE_API_KEY;
const ENV_BACKEND_LABEL = import.meta.env.VITE_BACKEND_LABEL;
const DEFAULT_API_BASE = '';

// Callback for dev mode to track last procedure call
type DevModeCallback = (procNum: string, params: Record<string, any>, sharedVars?: string) => void;
let devModeCallback: DevModeCallback | null = null;

/**
 * Dokleja tożsamość wołającego do każdego żądania.
 *
 * Backend (web_authz.i, audyt 2026-08-04) decyduje o dostępie do danych
 * kontrahenta na podstawie `sv_numrecuser`. Żądanie bez tego pola wygląda jak
 * "użytkownik bez listy" i przechodzi bez ograniczeń — a pojedyncze serwisy
 * potrafiły je zgubić (verification.service, invoices.service wcale;
 * ContractorSalesPage wysyłał pusty string). Każde takie pominięcie to dziura,
 * więc pole wstrzykujemy CENTRALNIE, zamiast pamiętać o nim w N miejscach.
 *
 * Jawna wartość w params ma pierwszeństwo — nadpisujemy tylko brak i pusty
 * string.
 *
 * Źródłem jest migawka TEJ karty (identity.service), nie localStorage per
 * żądanie: klucz `firmatic-auth` jest wspólny dla originu, a jeden origin
 * obsługuje dwie instancje Firmatic (ZKS preprod + produkcja), więc karta
 * produkcji potrafiła wysłać sv_numrecuser z bazy preprodu. Ten sam wzorzec, co
 * migawka konfiguracji BFF wyżej.
 */
function withIdentity(params: Record<string, any>): Record<string, any> {
  const merged = { ...params };
  const { employeeId, partnerId } = identityService.get();
  const identity: Record<string, string> = {};
  // pracownik
  if (employeeId) identity.sv_numrecuser = employeeId;
  // kontrahent zalogowany na siebie (portal) — backend wpuszcza go wyłącznie
  // na jego własne dane
  if (partnerId) identity.sv_numrecpartner = partnerId;

  for (const [key, value] of Object.entries(identity)) {
    const current = merged[key];
    if (current === undefined || current === null || String(current).trim() === '') {
      merged[key] = value;
    }
  }
  return merged;
}

/** Brama przekodowuje parametry do ISO-8859-2; znak spoza zestawu = HTTP 500. */
function toLatin2Params(params: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    out[key] = typeof value === 'string' ? sanitizeForLatin2(value) : value;
  }
  return out;
}

class ApiClient {
  private client: AxiosInstance;
  
  constructor() {
    // localStorage config (okienko) has priority over .env file
    const configBaseUrl = configService.getBaseUrl();
    const configApiKey = configService.getApiKey();
    const baseURL = configBaseUrl || ENV_API_URL || DEFAULT_API_BASE;
    const apiKey = configApiKey || ENV_API_KEY || '';
    
    // Log which backend is being used
    const label = ENV_BACKEND_LABEL || 'default';
    console.log(`[API] Initializing with backend: ${label} -> ${baseURL}`);
    
    // Build headers - add X-API-Key if configured (jak w wersji przed trybem proxy gateway – fbcaa0c)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    this.client = axios.create({
      baseURL: baseURL,
      timeout: 65000, // 65s (more than server timeout)
      headers: headers,
    });

    // Request interceptor: para {baseURL, X-API-Key} z JEDNEJ migawki konfiguracji.
    // baseURL idzie per żądanie razem z kluczem — osobne źródła tej pary
    // (baseURL ze startu karty, klucz z localStorage per request) dawały 401
    // na unigateway, gdy druga karta tego samego originu nadpisała config.
    this.client.interceptors.request.use(
      (config) => {
        const cfg = configService.getConfig();
        const baseUrl = configService.getBaseUrlFromConfig(cfg) || ENV_API_URL || DEFAULT_API_BASE;
        const currentApiKey = (cfg.apiKey || '') || ENV_API_KEY || '';
        const proxyMode = cfg.useProxy === true && typeof window !== 'undefined';
        const host = cfg.host.trim();
        const backendTarget = proxyMode && host ? `${cfg.protocol}://${host}:${cfg.port}` : '';

        config.baseURL = baseUrl;

        if (config.headers && typeof (config.headers as any).set === 'function') {
          const headers = config.headers as any;
          if (currentApiKey) {
            headers.set('X-API-Key', currentApiKey);
          } else {
            headers.delete('X-API-Key');
          }
          if (backendTarget) {
            headers.set('X-Backend-Target', backendTarget);
          } else {
            headers.delete('X-Backend-Target');
          }
        } else {
          config.headers = config.headers || ({} as any);
          const headers = config.headers as Record<string, string>;
          if (currentApiKey) {
            headers['X-API-Key'] = currentApiKey;
          } else {
            delete headers['X-API-Key'];
          }
          if (backendTarget) {
            headers['X-Backend-Target'] = backendTarget;
          } else {
            delete headers['X-Backend-Target'];
          }
        }

        // DEBUG: loguj nagłówki wychodzące
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, { apiKey: currentApiKey ? currentApiKey.slice(0,8)+'...' : '(brak)', proxyMode });
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        console.log(`[API] Response (${response.data?.execution_time_ms}ms):`, response.data);
        return response;
      },
      (error) => {
        console.error(`[API] Error:`, error.message);
        if (error.response) {
          console.error(`[API] Status: ${error.response.status}`, error.response.data);
        }
        return Promise.reject(error);
      }
    );
  }
  
  async execute<T = any>(procNum: string, params: Record<string, any> = {}, options?: { timeout?: number }): Promise<AblResponse<T>> {
    const request: AblRequest = {
      proc_num: procNum,
      params: toLatin2Params(withIdentity(params)),
    };
    
    const response = await this.client.post<AblResponse<T>>('/abl/execute', request, {
      ...(options?.timeout ? { timeout: options.timeout } : {}),
    });
    
    // Track procedure call for dev mode (callback handles isDeveloperMode check)
    if (devModeCallback) {
      try {
        const sharedVars = (response.data?.data as any)?.sharedvariables || '';
        devModeCallback(procNum, params, sharedVars);
      } catch (e) {
        console.error('[API] Dev mode callback error:', e);
      }
    }
    
    return response.data;
  }
  
  async healthCheck() {
    const response = await this.client.get('/health');
    return response.data;
  }
  
  /**
   * Update base URL and API key from current config (called after config save).
   * Config from okienko (localStorage) always takes priority.
   */
  updateBaseUrl() {
    const newBaseUrl = configService.getBaseUrl() || ENV_API_URL || DEFAULT_API_BASE;
    const apiKey = configService.getApiKey() || ENV_API_KEY || '';

    console.log('[API] updateBaseUrl() called, apiKey from config:', apiKey ? apiKey.slice(0, 8) + '...' : '(none)');

    this.client.defaults.baseURL = newBaseUrl;

    if (apiKey) {
      this.client.defaults.headers.common['X-API-Key'] = apiKey;
    } else {
      delete this.client.defaults.headers.common['X-API-Key'];
    }
    if (configService.isProxyGatewayMode()) {
      this.client.defaults.headers.common['X-Backend-Target'] = configService.getBackendTargetForHeader();
    } else {
      delete this.client.defaults.headers.common['X-Backend-Target'];
    }
    console.log('[API] updateBaseUrl() done. defaults.headers.common:', JSON.stringify(this.client.defaults.headers.common));
  }
  
  /**
   * Get current backend label (for display in UI)
   */
  getBackendLabel(): string {
    return ENV_BACKEND_LABEL || 'default';
  }

  /**
   * Set callback for dev mode to track procedure calls
   */
  setDevModeCallback(callback: DevModeCallback | null) {
    devModeCallback = callback;
  }
}

export const apiClient = new ApiClient();




